import { Barn, Horse, Owner, Person } from "../../models";
import { formatShortAddress } from "../address/FormatAddress";
import { getBarnById } from "../barn/Barn";
import { getPersonByPersonId } from "../person/Person";
import { formatDisplayNameForUser } from "../person/PersonNameFormat";
import { getOwnerById, getOwnersByBarnId, getOwnersByPersonId } from "./Owner";
export interface formattedOption {
    value: string; //Owner's id
    label: string; //Used for filtering options as a user types
    ownerName: string; //Owner's Full Name - will display when item is selected
    ownerNameLabel?: string; //Owner's Full Name
    barnLabel?: string; //Barn Name - will display in options
    locationLabel?: string //Owner's Location - will display in options
    createdByLabel?: string;
    owner?: Owner;
}

export interface formattedGroupOfOptions {
    label: string;
    options: formattedOption[];
}

export async function getOwnersCreatedByUserOrInCurrentBarn(user: Person, currentBarn?: (Barn | null | undefined), listOfOwnersCreatedByUser?: (formattedOption[] | undefined)) {
    // First, get owners by User (check if owners created by the user were already passed in)
    let ownersCreatedByUser: (formattedOption[] | undefined) = listOfOwnersCreatedByUser || [];
    if (ownersCreatedByUser.length === 0 && user) {
        ownersCreatedByUser = await getOwnersCreatedByUser(user);
    }

    // Next, get owners by Barn
    let ownersByBarn: (formattedOption[] | undefined) = [];
    if (currentBarn != null && currentBarn != undefined) {
        ownersByBarn = await getOwnersInCurrentBarn(currentBarn);
    }

    // Next, remove any duplicates by taking barn owners out of the list of created by owners
    const ownersCreatedByUserUpdatedList = await removeBarnOwnersFromCreatedByGroup(ownersCreatedByUser, ownersByBarn);

    const sortedCreatedByList = ownersCreatedByUserUpdatedList.sort((a, b) => a.ownerName.localeCompare(b.ownerName));
    const sortedBarnList = ownersByBarn?.sort((a, b) => a.ownerName.localeCompare(b.ownerName));

    const result = {
        "createdBy": sortedCreatedByList,
        "barn": sortedBarnList
    };

    return result;
};

export async function getOwnersCreatedByUser(user: Person) {
    try {
        let formattedList: formattedOption[] = [];
        const queryOwnersByCreatedByUser = await getOwnersByPersonId(user.id);
        if (queryOwnersByCreatedByUser.isSuccess) {
            const result: Owner[] = queryOwnersByCreatedByUser.result;
            formattedList = await formatOwners(result, user);
        }
        return formattedList;
    } catch (error) {
        console.error("Error fetching owners created by user:", error);
        return [];
    }
};

export async function getOwnersInCurrentBarn(currentBarn: Barn) {
    try {
        let formattedList: formattedOption[] = [];
        if (currentBarn) {
            const queryOwnersByBarn = await getOwnersByBarnId(currentBarn.id);
            if (queryOwnersByBarn.isSuccess) {
                const result: Owner[] = queryOwnersByBarn.result;
                formattedList = await formatOwners(result);
            }
        }
        return formattedList;
    } catch (error) {
        console.error("Error fetching barn owners:", error);
    }
};

export async function getOwnerForHorse(currentHorse: Horse) {
    try {
        let formattedOwner: formattedOption | null = null;
        const ownerId = currentHorse.ownerId || currentHorse.owner?.id;
        if (currentHorse && ownerId) {
            const queryOwnerForHorse = await getOwnerById(ownerId);
            if (queryOwnerForHorse.isSuccess) {
                const result: Owner = queryOwnerForHorse.result;
                formattedOwner = await formatSingleOwner(result);
            }
        }
        return formattedOwner;
    } catch (error) {
        console.error("Error fetching barn owners:", error);
    }
};

export async function removeBarnOwnersFromCreatedByGroup(createdByList?: (formattedOption[] | null), barnOwnersList?: (formattedOption[] | null)) {
    let result: formattedOption[] = createdByList || [];
    // Only need to handle this if both of the lists have items in them
    if (createdByList && createdByList.length > 0 && barnOwnersList && barnOwnersList.length > 0) {
        // Remove all of the barnOwners from the CreatedBy list
        result = createdByList?.filter((createdByOwner) => !barnOwnersList.some((barnOwner) => barnOwner.value === createdByOwner.value));
    }
    return result;
};

// FORMATTERS
export async function formatOwners(ownerArray: Owner[], user?: Person) {
    let formattedOwners = [];

    for (var i = 0; i < ownerArray.length; i++) {
        const owner = ownerArray[i];
        let object: formattedOption = await formatSingleOwner(owner, user);
        formattedOwners.push(object);
    }

    return formattedOwners;
};

export async function formatSingleOwner(owner: Owner, user?: Person) {
    const value = owner.id;
    const nameLabel = owner.name || "Unknown - No name found";
    const locationLabel = (owner.address ? formatShortAddress(owner.address) : (owner.location || "Unknown - No location found"));
    let createdByLabel = "Unknown";
    let barnLabel = "Unknown - No barn found";
    
    // If the owner is in the CreatedBy group, then we know the user who created it.
    // Saves us from querying the DB any more times than needed
    if (user) {
        createdByLabel = formatDisplayNameForUser(user);
    }
    
    // If we only have the personId, then we will make the DB query to get the createdBy person's name
    if (createdByLabel == "Unknown" && owner.createdBy) {
        const personResult = await getPersonByPersonId(owner.createdBy);
        if (personResult.isSuccess) {
            const person: Person = personResult.result;
            createdByLabel = formatDisplayNameForUser(person);
        }
    }

    // If we only have the barnId, then we will make the DB query to get the barnId
    if (barnLabel == "Unknown - No barn found" && owner.barnId) {
        const barnResult = await getBarnById(owner.barnId);
        if (barnResult.isSuccess) {
            const barn: Barn = barnResult.result;
            barnLabel = barn.name;
        }
    }

    let object: formattedOption = {
        value: value,
        label: nameLabel,
        ownerName: nameLabel,
        ownerNameLabel: nameLabel,
        barnLabel: barnLabel,
        locationLabel: locationLabel,
        createdByLabel: createdByLabel,
        owner: owner,
    };
    return object;
};